import { ChevronDown, ChevronRight } from 'lucide-preact'

/**
 * A section title in the asset review panel, which collapses its section on click (ORB-889).
 *
 * One component for both sections so "Quality check" and "Comments" cannot drift apart again — they
 * had already, one an `h4` at `text-[11px]` sitting inside its section's padding, the other an `h3`
 * at `text-xs` in its own bordered strip, so the panel read as one titled section and one loose
 * heading.
 *
 * ⚑ The collapsed state is NOT persisted. The overlay is a transient surface — opened on an asset,
 * closed, opened on the next — and a reviewer working through a batch would otherwise inherit a
 * state from three assets ago without remembering they set it. Decided with Joel on 2026-09-19 and
 * unchanged.
 *
 * ⚑⚑ What each section resets TO is the section's own business, and Quality check changed sides on
 * 2026-10-01 (ORB-987). This comment used to argue that resetting "means the findings are always the
 * thing you see first, which is the point of the panel" — which was right about the intent and wrong
 * about the effect. Opening on 43 findings is what made the panel read as a rejection rather than as
 * an assistant, so Quality check now resets to COLLAPSED with its open-finding count in the header,
 * and the count is what draws the eye when there is something to see. Asked for by James and Hyrzl
 * on the 1 Oct call; the design pass kept the count to findings only.
 */
interface Props {
    title: string
    /** Shown after the title, the way the comment thread has always shown its count. */
    count?: number
    collapsed: boolean
    onToggle: () => void
}

export function CollapsibleSectionHeader({ title, count, collapsed, onToggle }: Props) {
    const Chevron = collapsed ? ChevronRight : ChevronDown

    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            // ⚑⚑ flex-shrink-0 is what makes this an accordion rather than a long page with titles
            // in it. The header sits OUTSIDE its section's scroll container, so it stays put while
            // the content moves under it — before ORB-889 round 4 the section scrolled as one block
            // and the title you were reading under scrolled away with it.
            //
            // border-b only. The line ABOVE a section belongs to the section, not to its header, so
            // that a collapsed section still shows its divider with no content to draw one.
            className="w-full flex-shrink-0 flex items-center gap-1.5 px-4 py-2 border-b border-slate-200 dark:border-slate-700 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
        >
            <Chevron className="w-3.5 h-3.5 flex-shrink-0 text-slate-400 dark:text-slate-500" />
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                {title}{count !== undefined && count > 0 && ` (${count})`}
            </h3>
        </button>
    )
}
